// Keep only complete, numeric hour buckets. The renderer needs all 24 hours
// to calculate proportional segment widths safely.
const getHourlyPrices = (hourlyPrices = {}) =>
    Object.entries(hourlyPrices)
        .filter(
            ([hour, price]) =>
                /^(?:[01]\d|2[0-3])$/.test(hour) &&
                price !== null &&
                price !== undefined &&
                price !== '' &&
                Number.isFinite(Number(price))
        )
        .sort(([firstHour], [secondHour]) => firstHour.localeCompare(secondHour));

const formatHour = (hour) => {
    const hourNumber = Number(hour);
    const period = hourNumber >= 12 ? 'pm' : 'am';
    const displayHour = hourNumber % 12 || 12;

    return `${displayHour}${period}`;
};

const formatCompactHour = (hour) => {
    return formatHour(hour);
};

const formatPrice = (price) =>
    Number(price).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const getCurrentPricingState = (prices, now = new Date()) => {
    const groups = groupHourlyPrices(prices);
    const hour = now.getHours();
    const currentGroup = groups.find(
        ({ startHour, endHour }) => hour >= startHour && hour < endHour
    );
    const position = currentGroup
        ? ((currentGroup.startHour + currentGroup.endHour) / 2 / 24) * 100
        : 0;

    return {
        currentGroup,
        currentPrice: currentGroup ? formatPrice(currentGroup.price) : '',
        position,
    };
};

// Turn adjacent equal prices into one segment. A six-hour rate becomes one
// quarter of the bar instead of six visually repetitive cells.
const groupHourlyPrices = (prices) =>
    prices.reduce((groups, [hour, price]) => {
        const numericPrice = Number(price);
        const previousGroup = groups.at(-1);

        if (previousGroup?.price === numericPrice && previousGroup.endHour === Number(hour)) {
            previousGroup.endHour += 1;
            return groups;
        }

        groups.push({
            startHour: Number(hour),
            endHour: Number(hour) + 1,
            price: numericPrice,
        });

        return groups;
    }, []);

// A schedule earns the timeline only when all 24 hours are present and they are
// not all the same price.
const isVariableSchedule = (prices) =>
    prices.length === 24 && new Set(prices.map(([, price]) => Number(price))).size > 1;

export const hasVariableHourlyPricing = (hourlyPrices) =>
    isVariableSchedule(getHourlyPrices(hourlyPrices));

export const renderHourlyPricing = (
    hourlyPrices,
    { stationId = 'station', variant = 'card', headingLevel = 4 } = {}
) => {
    const prices = getHourlyPrices(hourlyPrices);

    // Uniform and incomplete schedules keep the legacy single-price display.
    if (!isVariableSchedule(prices)) {
        return '';
    }

    const headingTag = headingLevel === 3 ? 'h3' : 'h4';
    const headingId = `station-hourly-pricing-${stationId}`;
    const groups = groupHourlyPrices(prices);
    const { currentGroup, currentPrice, position } = getCurrentPricingState(prices);

    // The active segment, flag, and marker share the segment midpoint.
    const segments = groups
        .map(({ startHour, endHour, price }) => {
            const isCurrent = currentGroup?.startHour === startHour;
            const width = ((endHour - startHour) / 24) * 100;
            const priceLabel = formatPrice(price);
            const startLabel = formatHour(startHour % 24);
            const endLabel = formatHour(endHour % 24);

            return `
                <li class="station-hourly-pricing__segment${isCurrent ? ' is-current' : ''}${endHour - startHour <= 2 ? ' is-narrow' : ''}" data-start-hour="${startHour}" data-end-hour="${endHour}" style="width: ${width}%" aria-label="$${priceLabel} per kilowatt-hour from ${startLabel} to ${endLabel}">
                    <strong aria-hidden="true">
                        <span class="station-hourly-pricing__label-full">$${priceLabel}</span>
                        <span class="station-hourly-pricing__label-compact">$${priceLabel}</span>
                    </strong>
                </li>`;
        })
        .join('');
    const boundaries = [...new Set([0, ...groups.slice(1).map(({ startHour }) => startHour), 24])]
        .map(
            (hour) => `
                <span class="station-hourly-pricing__time${hour >= 22 && hour < 24 ? ' is-near-end' : ''}" style="left: ${(hour / 24) * 100}%">
                    <span class="station-hourly-pricing__label-full">${formatHour(hour % 24)}</span>
                    <span class="station-hourly-pricing__label-compact">${formatCompactHour(hour % 24)}</span>
                </span>`
        )
        .join('');
    const encodedPrices = encodeURIComponent(JSON.stringify(prices));
    const edgeClass = position < 10 ? ' is-near-start' : position > 90 ? ' is-near-end' : '';

    return `
        <section class="station-hourly-pricing station-hourly-pricing--${variant}" aria-labelledby="${headingId}" data-hourly-pricing-schedule="${encodedPrices}">
            <${headingTag} id="${headingId}">Price*</${headingTag}>
            <div class="station-hourly-pricing__chart${edgeClass}" style="--current-position: ${position}%">
                <div class="station-hourly-pricing__flag"><span aria-hidden="true"><span class="station-hourly-pricing__flag-label">Now</span> · $<span data-current-price>${currentPrice}</span></span><span class="visually-hidden" role="status" aria-live="polite" aria-atomic="true" data-current-price-announcement>Current price: $${currentPrice} per kilowatt-hour</span></div>
                <span class="station-hourly-pricing__marker" aria-hidden="true"></span>
                <ul class="station-hourly-pricing__segments">${segments}</ul>
                <div class="station-hourly-pricing__times" aria-hidden="true">${boundaries}</div>
            </div>
            <p class="station-hourly-pricing__note">*All rates are per kWh. Rate displayed on charger applies. See <a href="https://mercedesbenzhpc.com/power-hours/">Power Hours terms.</a></p>
        </section>`;
};

const updateHourlyPricing = (root = document, now = new Date()) => {
    root.querySelectorAll('[data-hourly-pricing-schedule]').forEach((section) => {
        let prices;

        try {
            prices = JSON.parse(decodeURIComponent(section.dataset.hourlyPricingSchedule));
        } catch {
            return;
        }

        const { currentGroup, currentPrice, position } = getCurrentPricingState(prices, now);
        const chart = section.querySelector('.station-hourly-pricing__chart');

        const price = section.querySelector('[data-current-price]');
        if (price && price.textContent !== currentPrice) {
            price.textContent = currentPrice;
            const announcement = section.querySelector('[data-current-price-announcement]');
            if (announcement) {
                announcement.textContent = `Current price: $${currentPrice} per kilowatt-hour`;
            }
        }

        section.querySelectorAll('[data-start-hour][data-end-hour]').forEach((segment) => {
            segment.classList.toggle(
                'is-current',
                Number(segment.dataset.startHour) === currentGroup?.startHour
            );
        });

        const segments = section.querySelector('.station-hourly-pricing__segments');
        const activeSegment = section.querySelector('.station-hourly-pricing__segment.is-current');
        let renderedPosition = position;
        if (segments && activeSegment) {
            const segmentsBounds = segments.getBoundingClientRect();
            const activeBounds = activeSegment.getBoundingClientRect();
            renderedPosition =
                ((activeBounds.left - segmentsBounds.left + activeBounds.width / 2) /
                    segmentsBounds.width) *
                100;
        }
        chart?.style.setProperty('--current-position', `${renderedPosition}%`);
        chart?.classList.toggle('is-near-start', renderedPosition < 10);
        chart?.classList.toggle('is-near-end', renderedPosition > 90);
    });
};

let updateTimer;

const scheduleHourlyPricingUpdates = () => {
    clearTimeout(updateTimer);
    updateTimer = setTimeout(() => {
        updateHourlyPricing();
        scheduleHourlyPricingUpdates();
    }, 60_000 - (Date.now() % 60_000));
};

export const initHourlyPricing = (root = document) => {
    // Detail pages provide a mount point and JSON data; map cards call the
    // renderer directly because their station data is already in memory.
    root.querySelectorAll('[data-hourly-pricing]').forEach((mount) => {
        let hourlyPrices;

        try {
            hourlyPrices = JSON.parse(mount.dataset.hourlyPrices || '{}');
        } catch {
            return;
        }

        const markup = renderHourlyPricing(hourlyPrices, {
            stationId: mount.dataset.stationId || 'station',
            variant: mount.dataset.hourlyPricingVariant || 'location',
            headingLevel: Number(mount.dataset.headingLevel) || 3,
        });

        mount.innerHTML = markup;

        if (markup) {
            mount
                .closest('.location-stats')
                ?.querySelector('[data-station-current-price]')
                ?.remove();
        }
    });

    updateHourlyPricing(root);
    scheduleHourlyPricingUpdates();
};
